import React, { useState, useEffect, useRef } from "react";

import RecipeActions from "./recipe/RecipeActions";
import RecipeSummary from "./recipe/RecipeSummary";
import RecipeImages from "./recipe/RecipeImages";
import RecipeInstructions from "./recipe/RecipeInstructions";
import RecipeIngredients from "./recipe/RecipeIngredients";
import RecipeTools from "./recipe/RecipeTools";
import ChefNotes from "./recipe/ChefNotes";
import { ClockIcon, UserGroupIcon } from "@heroicons/react/24/outline";
import axios from "../api/axiosConfig";

// Badge Colors
const categoryColors = {
  mealType: "bg-green-200 text-green-800",
  dietary: "bg-blue-200 text-blue-800",
  cuisine: "bg-purple-200 text-purple-800",
  cookingMethod: "bg-red-200 text-red-800",
};

export default function RecipeCard({ recipe }) {
  const [ingredients, setIngredients] = useState(recipe.ingredients || null);
  const [tools, setTools] = useState(recipe.tools || null);
  const [instructions, setInstructions] = useState(recipe.instructions || null);
  const [loadingIngredients, setLoadingIngredients] = useState(!recipe.ingredients);
  const [loadingTools, setLoadingTools] = useState(!recipe.tools);
  const [loadingInstructions, setLoadingInstructions] = useState(!recipe.instructions);
  const hasFetchedIngredients = useRef(false);
  const hasFetchedTools = useRef(false);
  const hasFetchedInstructions = useRef(false);

  const [activeTab, setActiveTab] = useState("ingredients"); // Default to Ingredients tab

  useEffect(() => {
    if (!recipe.id) return;

    // Fetch Ingredients if not already provided
    if (!recipe.ingredients && !hasFetchedIngredients.current) {
      hasFetchedIngredients.current = true;
      setLoadingIngredients(true);
      axios
        .post("/api/recipe/ingredients", { recipeId: recipe.id })
        .then((res) => setIngredients(res.data))
        .catch((err) => console.error("Error fetching ingredients:", err))
        .finally(() => setLoadingIngredients(false));
    }

    // Fetch Tools if not already provided
    if (!recipe.tools && !hasFetchedTools.current) {
      hasFetchedTools.current = true;
      setLoadingTools(true);
      axios
        .post("/api/recipe/tools", { recipeId: recipe.id })
        .then((res) => setTools(res.data))
        .catch((err) => console.error("Error fetching tools:", err))
        .finally(() => setLoadingTools(false));
    }

    // Fetch Instructions if not already provided
    if (!recipe.instructions && !hasFetchedInstructions.current) {
      hasFetchedInstructions.current = true;
      setLoadingInstructions(true);
      axios
        .post("/api/recipe/instructions", { recipeId: recipe.id })
        .then((res) => setInstructions(res.data))
        .catch((err) => console.error("Error fetching instructions:", err))
        .finally(() => setLoadingInstructions(false));
    }
  }, [recipe.id]);

  if (!recipe) return <p className="text-gray-500">Loading recipe...</p>;

  return (
    <div className="overflow-hidden">

      {/* Recipe Title and Save Button */}
      <div className="flex justify-between items-center py-2">
        <h2 className="text-xl sm:text-2xl font-semibold text-gray-800">{recipe.title}</h2>
        <RecipeActions recipe={recipe} />
      </div>

      {/* Image Carousel */}
      <RecipeImages recipeId={recipe.id} existingImages={recipe.images} defaultImage={recipe.originalRecipe.defaultImageUrl} />

      {/* Info Row */}
      <div className="text-sm text-gray-500 mb-4 mt-2 flex flex-col sm:flex-row sm:justify-between space-y-1 xs:space-y-0">
        <span className="flex items-center space-x-1">
          <ClockIcon className="w-5 h-5 text-gray-600" />
          <span>{recipe.cookTime || "N/A"}</span>
        </span>
        <div className="flex items-center space-x-1">
          <UserGroupIcon className="w-5 h-5 text-gray-600" />
          <span>{recipe.servings || "N/A"}</span>
        </div>
      </div>

      {/* Category Badges */}
      {recipe.categories && (
        <div className="flex flex-wrap gap-1 mb-4">
          {Object.entries(recipe.categories || {})
            .filter(([categoryKey]) => categoryKey !== "ingredients")
            .map(([categoryKey, values]) =>
              values.map((value, index) => (
                <span
                  key={`${categoryKey}-${index}`}
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColors[categoryKey] || "bg-gray-200 text-gray-800"
                    }`}
                >
                  {value}
                </span>
              ))
            )}
        </div>
      )}

      {/* Recipe Summary */}
      <RecipeSummary recipe={recipe} />

      <hr className="border-gray-300 mt-6" />

      {/* 📱 Tabs for Small Screens (`md:hidden`) */}
      <div className="block md:hidden mt-6">
        <div className="w-full overflow-x-auto scrollbar-hide">
          <div className="flex border-b border-gray-300 justify-between md:justify-around w-full min-w-max">
            {["ingredients", "instructions", "tools", "notes"].map((tab) => (
              <button
                key={tab}
                className={`py-2 px-4 text-sm font-medium flex-1 text-center ${activeTab === tab
                  ? "border-b-2 border-green-600 text-green-700"
                  : "text-gray-500"
                  }`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {activeTab === "ingredients" && (
            <RecipeIngredients ingredients={ingredients} loading={loadingIngredients} />
          )}
          {activeTab === "instructions" && (
            <RecipeInstructions instructions={instructions} loading={loadingInstructions} />
          )}
          {activeTab === "tools" && <RecipeTools tools={tools} loading={loadingTools} />}
          {activeTab === "notes" && <ChefNotes recipeId={recipe.id} />}
        </div>
      </div>

      {/* 🖥️ Large Screens Layout (`md:flex`) */}
      <div className="hidden md:flex flex-col lg:flex-row lg:space-x-8 mb-8">
        <div className="lg:w-2/3">
          <RecipeIngredients ingredients={ingredients} loading={loadingIngredients} />
        </div>
        <div className="lg:w-1/3">
          <RecipeTools tools={tools} loading={loadingTools} />
          <ChefNotes recipeId={recipe.id} />
        </div>
      </div>

      {/* ✅ Show Instructions for Large Screens Only */}
      <div className="hidden md:block">
        <hr className="border-gray-300 mb-6" />
        <RecipeInstructions instructions={instructions} loading={loadingInstructions} />
      </div>
    </div>
  );
}
